import { ApiError, NO_RESPONSE } from './client.js';
/**
 * WHAT TO RETRY, decided by what failed rather than by a single count.
 *
 * React Query's `retry: n` treats every failure the same, and the three kinds
 * this API produces want opposite handling:
 *
 *   NO RESPONSE     the request never got an answer — the api restarting, a
 *                   gateway error, a dropped link. Trying again is exactly the
 *                   right move, and once is not enough to cover a restart.
 *   4xx             the server answered and said no. Re-asking is pure latency,
 *                   and on a 401 it is a request against a session the client
 *                   has already ended.
 *   anything else   a 5xx that DID arrive, which may or may not be transient.
 *                   One more attempt, as before.
 *
 * Shared rather than written per app because both clients hit the same api and
 * the same restarts; two definitions of "worth retrying" would inevitably
 * disagree, and the one that mattered would be the one nobody looked at.
 *
 * @param unreachableAttempts how many times to re-ask when nothing answered.
 *   The caller chooses because the trade-off is genuinely different: a desktop
 *   console can afford to wait out a restart behind a spinner, while a handset
 *   in a dead zone is spending battery on a network that is not there.
 */
export function retryPolicy(unreachableAttempts) {
    return (failureCount, error) => {
        if (error instanceof ApiError) {
            if (error.status === NO_RESPONSE)
                return failureCount < unreachableAttempts;
            if (error.status >= 400 && error.status < 500)
                return false;
        }
        return failureCount < 1;
    };
}
/**
 * Exponential backoff with a CEILING, which is the part that matters.
 *
 * Uncapped doubling puts the later attempts minutes apart, so a retry budget
 * sized to cover a restart drifts past the end of it and the user sees the error
 * anyway. Capped at ten seconds, six attempts span roughly thirty-five — which
 * is about what an api component takes to go away and finish booting.
 */
export function retryDelay(attempt) {
    return Math.min(1_000 * 2 ** attempt, 10_000);
}
//# sourceMappingURL=retry.js.map